import { request } from "../utils/request";
import { JobStatus } from "../utils/status";
import { BACKEND_BASE_URL } from "./Resolver";
import { APIResource } from "./Resource";

export type TaskRecord = {
  _id: string;
  job_id: string;
  encode_id?: string | null;
  template: string;
  segment_size: number;
  version: number;
  renditions: RenditionRecord[] | null;
  progress: number;
  submit_time: string;
  start_time?: string | null;
  finish_time?: string | null;
  status: string;
  error: string;
  error_type: string;
};

export const errorCodeMappings: { [key: string]: string } = {
  TRANSCODE_CLIENT_FAILURE: "Transcoding failure",
  STATUS_POLL_FAILURE: "Status retrieval failure",
  DELETE_MEDIA_FILES_FAILURE: "Media file(s) deletion failure",
  MOVE_RENDITIONS_FAILURE: "Rendition(s) archival failure",
};

export type RenditionRecord = {
  filename: string;
  playback_type: string;
  registered_url: string;
  primary_registered_regions: string[];
  secondary_registered_regions: string[] | null;
};

export type JobsResponse = {
  count: number;
  items: Job[];
};

type JobCreateResponse = {
  creative_id: string;
  id: string;
};

export class Job extends APIResource {
  upsertFields = [
    "name",
    "source",
    "session_id",
    "network_id",
    "source_creative_rendition_id",
    "content_type",
    "creative_rendition_id_key",
    "creative_rendition_id_value",
  ];
  baseURL = `${BACKEND_BASE_URL}/api/v1/transcode`;

  _id: string;
  creative_id: string;
  created_date: string;
  backfill_date?: string | null;
  source: string;
  session_id: string;
  name: string;
  network_id: string;
  source_creative_rendition_id: number;
  content_type: string;
  creative_rendition_id_key: string;
  creative_rendition_id_value: string;
  status: JobStatus;
  tasks: TaskRecord[] | null;
  ad_server_request?: string | null;
  ad_server_response?: string | null;
  dry_run: boolean;
  backfill_pending?: boolean | null;
  notification_date?: string | null;
  error?: string | null;
  deactivation_date?: string | null;
  set_id?: string | null;

  constructor({
    _id,
    creative_id,
    created_date,
    backfill_date,
    source,
    session_id,
    name,
    network_id,
    source_creative_rendition_id,
    content_type,
    creative_rendition_id_key,
    creative_rendition_id_value,
    status,
    tasks,
    ad_server_request,
    ad_server_response,
    dry_run,
    backfill_pending,
    notification_date,
    error,
    deactivation_date,
    set_id,
  }: {
    _id: string;
    creative_id: string;
    created_date: string;
    backfill_date?: string | null;
    source: string;
    session_id: string;
    name: string;
    network_id: string;
    source_creative_rendition_id: number;
    content_type: string;
    creative_rendition_id_key: string;
    creative_rendition_id_value: string;
    status: JobStatus;
    tasks: TaskRecord[] | null;
    ad_server_request?: string | null;
    ad_server_response?: string | null;
    dry_run: boolean;
    backfill_pending?: boolean | null;
    notification_date?: string | null;
    error?: string | null;
    deactivation_date?: string | null;
    set_id?: string | null;
  }) {
    super();
    this._id = _id;
    this.creative_id = creative_id;
    this.created_date = created_date;
    this.backfill_date = backfill_date;
    this.source = source;
    this.session_id = session_id;
    this.name = name;
    this.network_id = network_id;
    this.source_creative_rendition_id = source_creative_rendition_id;
    this.content_type = content_type;
    this.creative_rendition_id_key = creative_rendition_id_key;
    this.creative_rendition_id_value = creative_rendition_id_value;
    this.status = status;
    this.tasks = tasks;
    this.ad_server_request = ad_server_request;
    this.ad_server_response = ad_server_response;
    this.dry_run = dry_run;
    this.backfill_pending = backfill_pending;
    this.notification_date = notification_date;
    this.error = error;
    this.deactivation_date = deactivation_date;
    this.set_id = set_id;
  }

  get id(): string {
    return this._id;
  }

  set id(value: string) {
    this._id = value;
  }

  session_id__validation(value: string): string | undefined {
    if (!value) return "Should not be empty.";
  }

  name__validation(value: string): string | undefined {
    if (!value) return "Should not be empty.";
  }

  network_id__validation(value: string): string | undefined {
    if (!value) return "Should not be empty.";
  }

  source_creative_rendition_id__validation(value: string): string | undefined {
    if (!value) return "Should not be empty.";
  }

  source__validation(value: string): string | undefined {
    if (!value) return "Should not be empty.";
    try {
      new URL(value);
    } catch (_) {
      return "Should be valid URL.";
    }
  }

  content_type__validation(value: string): string | undefined {
    if (!value) return "Should not be empty.";
  }

  creative_rendition_id_key__validation(value: string): string | undefined {
    if (!value) return "Should not be empty.";
  }

  creative_rendition_id_value__validation(value: string): string | undefined {
    if (!value) return "Should not be empty.";
  }

  getUpsertXML = () => `<?xml version="1.0" encoding="UTF-8"?>
<transcode>
  <sessionId>${this.session_id}</sessionId>
  <creative>
    <name>${this.name}</name>
    <networkId>${this.network_id}</networkId>
    <sourceCreativeRendition>
      <id>${this.source_creative_rendition_id}</id>
      <location>${this.source}</location>
    </sourceCreativeRendition>
    <creativeRendition>
      <contentType>${this.content_type}</contentType>
      <id3>
        <key>${this.creative_rendition_id_key}</key>
        <value>${this.creative_rendition_id_value}</value>
      </id3>
    </creativeRendition>
  </creative>
</transcode>`;

  // It is important to have the following function as arrow functions.
  // Regular function is losing "this" context when passing the methods as callbacks.
  canRegisterAssets = (): boolean => {
    return [JobStatus.SUCCESS, JobStatus.UNSUCCESSFUL, JobStatus.COMPLETE, JobStatus.FAILED].includes(
      this.status,
    );
  };

  canReTranscodeJob = (): boolean => {
    return [
      JobStatus.SUCCESS,
      JobStatus.UNSUCCESSFUL,
      JobStatus.COMPLETE,
      JobStatus.FAILED,
      JobStatus.ERROR,
    ].includes(this.status);
  };

  async create(): Promise<JobCreateResponse> {
    const baseURL = `${BACKEND_BASE_URL}/api/v1/transcode`;
    const url = new URL(`${baseURL}`);
    const response = await request(url, { method: "POST", body: this.getUpsertXML() }, true);
    return (await response.json()) as JobCreateResponse;
  }

  async update() {
    // Shouldn't be possible to update a Job.
    throw new Error("Not implemented");
  }

  async delete() {
    // Shouldn't be possible to delete a Job.
    throw new Error("Not implemented");
  }
}

export class Jobs {
  private static baseURL = `${BACKEND_BASE_URL}/api/v1`;

  /**
   * Fetches a list of jobs with customizable query parameters.
   *
   * Sends a GET request to the API's jobs endpoint. Accepts flexible query parameters for
   * customizing the response, such as pagination. Common parameters include 'page' and 'limit'.
   * Also, can be used with filters like 'status', etc...
   *
   * @param {object} options - Contains query parameters as key-value pairs (e.g., { page: 2, limit: 10 }).
   * Provides flexibility in request customization. Defaults to an empty object if no options are specified.
   *
   * @returns {Promise<JobsResponse>} Promise resolving to the jobs list response.
   *
   * Example:
   * list({ page: 1, limit: 25, status: "ERROR" }).then(console.log).catch(console.error);
   */
  static async list(options: { [key: string]: any } = {}): Promise<JobsResponse> {
    const url = new URL(`${this.baseURL}/jobs`);
    url.search = new URLSearchParams(
      Object.entries(options).flatMap(([key, values]) =>
        Array.isArray(values) ? values.map((value) => [key, value]) : [[key, values]],
      ),
    ).toString();
    if (options.statuses) {
      options.statuses.map((status: string) => url.searchParams.append("status", status));
      url.searchParams.delete("statuses");
    }
    if (options.sets) {
      options.sets.map((setID: string) => url.searchParams.append("setid", setID));
      url.searchParams.delete("sets");
    }
    const response = await request(url, { method: "GET" }, true);
    const json = await response.json();
    const jobs = json.items?.map((item: Job) => new Job(item));
    return { count: json.count, items: jobs };
  }

  /**
   * Retrieves detailed information for a specific job by its ID.
   *
   * This function sends a GET request to the API's job endpoint for a single job, using the job's ID.
   * It's used to fetch detailed information about a job, such as its tasks array, etc...
   *
   * @param {string} id - The unique identifier of the job to retrieve.
   *
   * @returns {Promise<any>} A promise resolving to the detailed info of the job.
   *
   * Example:
   * retrieve("65fcc04e50c2e5cdcf766ea1").then(console.log).catch(console.error);
   */
  static async retrieve(id: string): Promise<Job> {
    const url = new URL(`${this.baseURL}/jobs/${id}`);
    const response = await request(url, { method: "GET" }, true);
    const json = await response.json();
    return new Job(json.job);
  }
}
